import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/InventoryReports.css';

function InventoryReports() {
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventoryData, setInventoryData] = useState([]);
  const [masterInventory, setMasterInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    category: '',
    item_type: '',
    staff_id: '',
    condition: '',
    status: '',
    search: ''
  });

  const [staffList, setStaffList] = useState([]);
  
  // Send Report Modal
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendFormat, setSendFormat] = useState('csv');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadData();
    loadStaff();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (activeTab === 'inventory') {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/inventory`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Inventory data loaded:', response.data);
        setInventoryData(response.data);
      } else if (activeTab === 'master') {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/inventory-items/master/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Master inventory loaded:', response.data);
        setMasterInventory(response.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/staff?clientCode=caam`);
      setStaffList(response.data);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      item_type: '',
      staff_id: '',
      condition: '',
      status: '',
      search: ''
    });
  };

  const getFilteredData = () => {
    let filtered = [...inventoryData];
    
    if (filters.category) {
      filtered = filtered.filter(item => item.category === filters.category);
    }
    if (filters.item_type) {
      filtered = filtered.filter(item => 
        item.item_type?.toLowerCase().includes(filters.item_type.toLowerCase())
      );
    }
    if (filters.staff_id) {
      if (filters.staff_id === 'unassigned') {
        filtered = filtered.filter(item => !item.staff_id);
      } else {
        filtered = filtered.filter(item => item.staff_id === parseInt(filters.staff_id));
      }
    }
    if (filters.condition) {
      filtered = filtered.filter(item => item.condition === filters.condition);
    }
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item => 
        item.description?.toLowerCase().includes(searchLower) ||
        item.item_type?.toLowerCase().includes(searchLower) ||
        item.brand?.toLowerCase().includes(searchLower) ||
        item.model?.toLowerCase().includes(searchLower) ||
        item.serial_number?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  };

  const toggleRecipient = (staffId) => {
    setSelectedRecipients(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const handleSendReport = async () => {
    if (selectedRecipients.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const data = getFilteredData();
      
      // Create report based on format
      let reportData;
      const headers = ['ID', 'Category', 'Type', 'Description', 'Brand', 'Model', 'Serial', 'Quantity', 'Condition', 'Status'];
      reportData = [
        headers.join(','),
        ...data.map(item => [
          item.id,
          item.category || '',
          item.item_type || '',
          `"${(item.description || '').replace(/"/g, '""')}"`,
          item.brand || '',
          item.model || '',
          item.serial_number || '',
          item.quantity,
          item.condition || '',
          item.status || ''
        ].join(','))
      ].join('\n');
      
      // Send via notifications to each recipient
      for (const recipientId of selectedRecipients) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/notifications`,
          {
            recipient_id: recipientId,
            subject: `Inventory Report - ${new Date().toLocaleDateString()}`,
            message: `Attached is the inventory report you requested.\n\nFormat: ${sendFormat.toUpperCase()}\nItems: ${data.length}\nGenerated: ${new Date().toLocaleString()}`,
            type: 'report',
            attachment_data: reportData,
            attachment_format: sendFormat
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      alert(`Report sent successfully to ${selectedRecipients.length} recipient(s)!`);
      setShowSendModal(false);
      setSelectedRecipients([]);
    } catch (error) {
      console.error('Error sending report:', error);
      alert('Failed to send report: ' + (error.response?.data?.error || error.message));
    } finally {
      setSending(false);
    }
  };

  const renderInventoryTab = () => {
    const filteredData = getFilteredData();
    
    return (
      <div className="tab-content">
        <div className="reports-layout">
          <div className="filters-sidebar">
            <h3>Filters</h3>
            
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search anything..."
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Category</label>
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Categories</option>
                <option value="furniture">Furniture</option>
                <option value="computer">Computer</option>
                <option value="equipment">Equipment</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Item Type</label>
              <input
                type="text"
                name="item_type"
                value={filters.item_type}
                onChange={handleFilterChange}
                placeholder="Desk, Chair, etc."
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Assigned To</label>
              <select
                name="staff_id"
                value={filters.staff_id}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Staff</option>
                <option value="unassigned">Unassigned</option>
                {staffList.map(staff => (
                  <option key={staff.id} value={staff.id}>
                    {staff.first_name} {staff.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Condition</label>
              <select
                name="condition"
                value={filters.condition}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Conditions</option>
                <option value="New">New</option>
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="available">Available</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </select>
            </div>

            <button onClick={clearFilters} className="btn-clear-filters">
              Clear All Filters
            </button>
          </div>

          <div className="reports-main">
            <div className="reports-header">
              <div>
                <h2>Inventory Report</h2>
                <p>{filteredData.length} items found</p>
              </div>
              <button onClick={() => setShowSendModal(true)} className="btn-send">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13"/>
                  <path d="M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
                Send Report
              </button>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Brand</th>
                    <th>Model</th>
                    <th>Serial #</th>
                    <th>Qty</th>
                    <th>Condition</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" style={{textAlign: 'center', padding: '40px'}}>Loading...</td>
                    </tr>
                  ) : filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{textAlign: 'center', padding: '40px', color: '#64748b'}}>
                        No items found. Try adjusting your filters.
                      </td>
                    </tr>
                  ) : (
                    filteredData.map(item => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td style={{textTransform: 'capitalize'}}>{item.category}</td>
                        <td>{item.item_type}</td>
                        <td>{item.description || '-'}</td>
                        <td>{item.brand || '-'}</td>
                        <td>{item.model || '-'}</td>
                        <td>{item.serial_number || '-'}</td>
                        <td>{item.quantity}</td>
                        <td>
                          <span className={`badge badge-${item.condition?.toLowerCase()}`}>
                            {item.condition || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-${item.status}`}>
                            {item.status || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMasterTab = () => {
    return (
      <div className="tab-content">
        <div className="reports-header">
          <div>
            <h2>Master Inventory Summary</h2>
            <p>Track remaining quantities for audit</p>
          </div>
        </div>

        <div className="master-inventory-grid">
          {loading ? (
            <div style={{padding: '40px', textAlign: 'center', gridColumn: '1 / -1'}}>Loading...</div>
          ) : masterInventory.length === 0 ? (
            <div style={{padding: '40px', textAlign: 'center', color: '#64748b', gridColumn: '1 / -1'}}>
              No master inventory found. Add items via "Add Inventory Item" with "Assigned To" set to "Unassigned".
            </div>
          ) : (
            masterInventory.map(item => (
              <div key={item.id} className="master-card">
                <div className="master-card-header">
                  <h3>{item.item_type}</h3>
                  <span className={`qty-badge ${item.quantity === 0 ? 'empty' : ''}`}>
                    {item.quantity} remaining
                  </span>
                </div>
                <p className="master-description">{item.description || 'No description'}</p>
                <div className="master-stats">
                  <div className="stat">
                    <span className="stat-label">Times Assigned</span>
                    <span className="stat-value">{item.times_assigned || 0}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Added</span>
                    <span className="stat-value">{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderInsightsTab = () => {
    return (
      <div className="tab-content">
        <div className="reports-header">
          <h2>Inventory Insights</h2>
          <p>Analytics and trends</p>
        </div>
        <div style={{padding: '40px', textAlign: 'center', color: '#64748b'}}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{margin: '0 auto 16px'}}>
            <path d="M3 3v18h18"/>
            <path d="M18 17V9"/>
            <path d="M13 17V5"/>
            <path d="M8 17v-3"/>
          </svg>
          <p>Insights & Analytics coming soon!</p>
        </div>
      </div>
    );
  };

  const renderAuditTab = () => {
    return (
      <div className="tab-content">
        <div className="reports-header">
          <h2>Audit Trail</h2>
          <p>Complete history of inventory events</p>
        </div>
        <div style={{padding: '40px', textAlign: 'center', color: '#64748b'}}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{margin: '0 auto 16px'}}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          <p>Audit Trail coming soon!</p>
        </div>
      </div>
    );
  };

  return (
    <div className="inventory-reports">
      <div className="tabs-header">
        <div className="tabs-nav">
          <button
            className={`tab-button ${activeTab === 'inventory' ? 'active' : ''}`}
            onClick={() => setActiveTab('inventory')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
            Inventory Report
          </button>
          <button
            className={`tab-button ${activeTab === 'master' ? 'active' : ''}`}
            onClick={() => setActiveTab('master')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 7h-7"/>
              <path d="M14 17H4a2 2 0 0 1-2-2V5c0-1.1.9-2 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2z"/>
              <line x1="12" y1="17" x2="12" y2="22"/>
              <path d="M8 22h8"/>
            </svg>
            Master Inventory
          </button>
          <button
            className={`tab-button ${activeTab === 'insights' ? 'active' : ''}`}
            onClick={() => setActiveTab('insights')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18"/>
              <path d="M18 17V9"/>
              <path d="M13 17V5"/>
              <path d="M8 17v-3"/>
            </svg>
            Insights
          </button>
          <button
            className={`tab-button ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Audit Trail
          </button>
        </div>
      </div>

      {activeTab === 'inventory' && renderInventoryTab()}
      {activeTab === 'master' && renderMasterTab()}
      {activeTab === 'insights' && renderInsightsTab()}
      {activeTab === 'audit' && renderAuditTab()}

      {/* Send Report Modal */}
      {showSendModal && (
        <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send Inventory Report</h3>
              <button className="modal-close" onClick={() => setShowSendModal(false)}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Report Format</label>
                <div className="format-options">
                  <label className={`format-option ${sendFormat === 'csv' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="format"
                      value="csv"
                      checked={sendFormat === 'csv'}
                      onChange={(e) => setSendFormat(e.target.value)}
                    />
                    <span>CSV</span>
                  </label>
                  <label className={`format-option ${sendFormat === 'xlsx' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="format"
                      value="xlsx"
                      checked={sendFormat === 'xlsx'}
                      onChange={(e) => setSendFormat(e.target.value)}
                    />
                    <span>XLSX</span>
                  </label>
                  <label className={`format-option ${sendFormat === 'pdf' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="format"
                      value="pdf"
                      checked={sendFormat === 'pdf'}
                      onChange={(e) => setSendFormat(e.target.value)}
                    />
                    <span>PDF</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Send To ({selectedRecipients.length} selected)</label>
                <div className="recipients-list">
                  {staffList.map(staff => (
                    <label key={staff.id} className="recipient-item">
                      <input
                        type="checkbox"
                        checked={selectedRecipients.includes(staff.id)}
                        onChange={() => toggleRecipient(staff.id)}
                      />
                      <span>{staff.first_name} {staff.last_name}</span>
                      <span className="recipient-email">{staff.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowSendModal(false)}>
                Cancel
              </button>
              <button className="btn-send-confirm" onClick={handleSendReport} disabled={sending}>
                {sending ? 'Sending...' : 'Send Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryReports;